/**
 * The supporting-document refusals an agent dispatch can return, shared by all five
 * agent pages: "still converting" (409, `supporting_docs_converting`), "a document is
 * broken" (409, `supporting_docs_failed`) and "too large for the model" (400,
 * `supporting_docs_over_cap`). All three are fixed on the product's documents list, so
 * every toast carries a button that goes there instead of leaving the user with a
 * sentence and no place to go.
 *
 * Broken is kept separate from still-converting deliberately: converting clears itself,
 * broken never does, and telling someone to "wait for the check marks" on a document
 * that can never earn one is worse than saying nothing.
 */
import { toast } from 'sonner';

import { ApiConflictError, ApiError } from '@/lib/api-client';


/** DOM id of the Supporting Documents section on the product page — the target of the
 *  "Check documents" button. Lives here (not in the route file) so the route stays
 *  code-splittable. */
export const SUPPORTING_DOCS_SECTION_ID = 'supporting-documents';

// The "scroll to the documents" intent travels in memory, NOT in the URL: a `#hash`
// makes the browser and the router jump to the element natively, and that jump moves
// the whole window — on a display taller than the app shell it left the page hanging
// over empty space.
//
// A TIMESTAMP, not a boolean, and reading it does not clear it. React StrictMode mounts
// every component twice in dev (mount -> effects -> cleanup -> mount): a read-and-clear
// flag was spent by the first mount and gone by the second, so the landing worked only
// when the first attempt's scroll happened to survive the teardown. That is dev-only —
// production has no StrictMode — which is why it looked random and why production browser
// tests never caught it. The window keeps a stale intent from hijacking a later visit.
const FOCUS_INTENT_MS = 10_000;
let pendingFocusAt: number | null = null;

export function requestSupportingDocsFocus(): void {
  pendingFocusAt = Date.now();
}

/** Is a fresh "show me the documents" intent outstanding? Does NOT clear it — see above.
 *  Call ``clearSupportingDocsFocus`` once the scroll has actually settled. */
export function consumeSupportingDocsFocus(): boolean {
  return pendingFocusAt != null && Date.now() - pendingFocusAt < FOCUS_INTENT_MS;
}

/** The intent has been honoured (or abandoned) — a later visit must not act on it. */
export function clearSupportingDocsFocus(): void {
  pendingFocusAt = null;
}

export interface SupportingDocsConverting {
  productId: number;
  pending: number;
  message: string;
}

/** The structured `detail` object of an API error, whichever way the client wrapped it. */
export function structuredDetail(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError)) return null;
  const raw = err.detail as { detail?: unknown } | undefined;
  const d = raw && typeof raw === 'object' && 'detail' in raw ? raw.detail : raw;
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : null;
}

/** Pure: the gate's structured 409, or null for any other error. */
export function supportingDocsConvertingFromConflict(err: unknown): SupportingDocsConverting | null {
  if (!(err instanceof ApiConflictError)) return null;
  const d = structuredDetail(err);
  if (!d || d.reason !== 'supporting_docs_converting') return null;
  const productId = typeof d.product_id === 'number' ? d.product_id : null;
  if (productId == null) return null;
  return {
    productId,
    pending: typeof d.pending_supporting_docs === 'number' ? d.pending_supporting_docs : 0,
    message: typeof d.message === 'string' && d.message ? d.message : 'Supporting documents are still converting.',
  };
}

export interface SupportingDocsFailed {
  productId: number;
  filenames: string[];
  message: string;
}

/** Pure: the "a supporting document could not be prepared" 409, or null. */
export function supportingDocsFailedFromConflict(err: unknown): SupportingDocsFailed | null {
  if (!(err instanceof ApiConflictError)) return null;
  const d = structuredDetail(err);
  if (!d || d.reason !== 'supporting_docs_failed') return null;
  const productId = typeof d.product_id === 'number' ? d.product_id : null;
  if (productId == null) return null;
  const raw = d.failed_supporting_docs;
  return {
    productId,
    filenames: Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [],
    message:
      typeof d.message === 'string' && d.message
        ? d.message
        : 'A supporting document could not be prepared for the agents.',
  };
}

export interface SupportingDocsOverCap {
  productId: number;
  totalChars: number;
  maxChars: number;
  largest: { filename: string; chars: number }[];
}

/** Pure: the "documents exceed the model's budget" 400, or null for any other error. */
export function supportingDocsOverCapFromError(err: unknown): SupportingDocsOverCap | null {
  const d = structuredDetail(err);
  if (!d || d.reason !== 'supporting_docs_over_cap' || typeof d.product_id !== 'number') return null;
  const largest = Array.isArray(d.largest)
    ? d.largest.filter(
        (x): x is { filename: string; chars: number } =>
          !!x && typeof x === 'object' && typeof (x as { filename?: unknown }).filename === 'string',
      )
    : [];
  return {
    productId: d.product_id,
    totalChars: typeof d.total_chars === 'number' ? d.total_chars : 0,
    maxChars: typeof d.max_chars === 'number' ? d.max_chars : 0,
    largest,
  };
}


/** Show whichever supporting-document refusal `err` is, with a button to the product's
 *  documents. Returns true when it was one (the caller stops there), false to fall through. */
export function notifySupportingDocsRefusal(
  err: unknown,
  goToProduct: (productId: number) => void,
): boolean {
  const go = (id: number) => {
    requestSupportingDocsFocus();
    goToProduct(id);
  };
  const converting = supportingDocsConvertingFromConflict(err);
  if (converting) {
    // One short line + the button; the server's fuller sentence is for API callers.
    const n = converting.pending;
    toast.warning(
      n > 0 ? `${n} supporting document${n === 1 ? ' is' : 's are'} still converting.` : converting.message,
      { duration: 10_000, action: { label: 'Check documents', onClick: () => go(converting.productId) } },
    );
    return true;
  }
  const failed = supportingDocsFailedFromConflict(err);
  if (failed) {
    // Deliberately the SAME shape as the converting toast above — one line plus the
    // action, no `description`. Two shapes for one control is how they drift apart.
    const n = failed.filenames.length;
    const names = failed.filenames.slice(0, 2).join(', ');
    toast.warning(
      n === 1
        ? `${names || 'A supporting document'} could not be prepared — remove or replace it to run.`
        : `${n} supporting documents could not be prepared — remove or replace them to run.`,
      { duration: 15_000, action: { label: 'Check documents', onClick: () => go(failed.productId) } },
    );
    return true;
  }
  const over = supportingDocsOverCapFromError(err);
  if (over) {
    const top = over.largest.slice(0, 3).map((x) => x.filename).join(', ');
    toast.error('These documents exceed what the agents can use.', {
        description: `Remove or shorten documents, then run again.${top ? ` Largest: ${top}.` : ''}`,
        duration: 15_000,
        action: { label: 'Check documents', onClick: () => go(over.productId) },
      },
    );
    return true;
  }
  return false;
}
